import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Box,
  Divider,
  useToast,
  Textarea,
  Text,
  VStack,
  HStack,
  Badge
} from 'components-lib';
import { axiosInstance } from 'config/api';
import { humanizeApiError } from '../../../utils/api_error_samples';

const FileUploadField = ({
  label,
  id,
  name,
  onChange,
  selectedFile,
  helperText = 'Supported: JPG, PNG, PDF'
}) => (
  <FormControl>
    <FormLabel>{label}</FormLabel>
    <Input
      id={id}
      type="file"
      name={name}
      onChange={onChange}
      // accept="image/*"
      display="none"
    />
    <HStack align="center" spacing={3} flexWrap="wrap">
      <Button
        as="label"
        htmlFor={id}
        bg="gray.200"
        color="gray.800"
        borderRadius="md"
        px={4}
        py={1}
        fontSize="sm"
        _hover={{ bg: 'gray.300' }}
        _active={{ bg: 'gray.400' }}
      >
        Choose File
      </Button>
      <Text fontSize="sm" color="gray.600" maxW="180px" isTruncated>
        {selectedFile ? selectedFile.name : 'No file chosen yet'}
      </Text>
    </HStack>
    <Text fontSize="xs" color="gray.500" mt={1}>{helperText}</Text>
  </FormControl>
);

const AddClinicModal = ({ isOpen, onClose, onClinicCreated }) => {
  const [form, setForm] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    whatsapp: '',
    logo: null,
    barCode: null,
    qrCode: null,
    advice: null,
    payment: {
      upi: '',
      bank: {
        account_holder: '',
        account_number: '',
        bank_name: '',
        ifsc_code: '',
      }
    }
  });
  const [loading, setLoading] = useState(false);
  const [uploadedAdvices, setUploadedAdvices] = useState([]);
  const [currentClinicId, setCurrentClinicId] = useState(null);
  const [showDoneButton, setShowDoneButton] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const toast = useToast();





  // Function 1: Upload file to external API
  const uploadFile = async (file) => {
    const formData = new FormData();
    // const fileName = `uploaded-file-${Date.now()}.${file.name.split('.').pop()}`;
    formData.append("file", file);

    try {
      const uploadResponse = await axiosInstance.post(
        "/api/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            // "Authorization": `Bearer ${token}`, // Ensure `token` is available in the scope
          },
          maxBodyLength: Infinity,
        }
      );
      return uploadResponse?.data?.objectUrl;
    } catch (error) {
      console.error("File upload failed:", error);
      throw new Error("Failed to upload file.");
    }
  };

  // Use central humanization helper from utils/api_error_samples



  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        [name]: value
      }
    }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        bank: {
          ...prev.payment.bank,
          [name]: value
        }
      }
    }));
  };

  const handleLogoChange = (e) => {
    setForm(prev => ({ ...prev, logo: e.target.files[0] }));
  };

  const handleBarCodeChange = (e) => {
    setForm(prev => ({ ...prev, barCode: e.target.files[0] }));
  };

  const handleQrCodeChange = (e) => {
    setForm(prev => ({ ...prev, qrCode: e.target.files[0] }));
  };



  const resetForm = () => {
    setForm({
      name: '',
      address: '',
      email: '',
      phone: '',
      whatsapp: '',
      logo: null,
      barCode: null,
      qrCode: null,
      advice: null,
      payment: {
        upi: '',
        bank: {
          account_holder: '',
          account_number: '',
          bank_name: '',
          ifsc_code: '',
        }
      }
    });
    setUploadedAdvices([]);
    setCurrentClinicId(null);
    setShowDoneButton(false);
    setFieldErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };







  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setLoading(true);
    try {
      // 1. Prepare clinic details for addClinic
      const clinicData = {
        clinic_name: form.name,
        address: form.address,
        upi_details: form.payment.upi,
        bank_details: `Account No: ${form.payment.bank.account_number}, IFSC: ${form.payment.bank.ifsc_code}`,
        whatsapp_no: form.whatsapp,
        phoneno: form.phone,
        clinic_email: form.email,
      };
      console.log('Sending clinic data:', clinicData);
      const addClinicRes = await axiosInstance.post('/api/clinic/addClinic', clinicData);
      console.log('Add clinic response:', addClinicRes.data);
      const clinicId = addClinicRes.data.id || addClinicRes.data._id || addClinicRes.data.clinic_id;
      if (!clinicId) throw new Error('No clinic id returned from addClinic');

      // Store the clinic ID for later use
      setCurrentClinicId(clinicId);

      // 2. Upload files first to get URLs, then POST those URLs to /api/clinic/uploadFiles
      const fileUrls = {};

      try {
        if (form.logo) {
          fileUrls.clinic_icon = await uploadFile(form.logo);
          console.log('Uploaded logo URL:', fileUrls.clinic_icon);
        }
        if (form.barCode) {
          fileUrls.bar_code = await uploadFile(form.barCode);
          console.log('Uploaded bar code URL:', fileUrls.bar_code);
        }
        if (form.qrCode) {
          fileUrls.qr_code = await uploadFile(form.qrCode);
          console.log('Uploaded qr code URL:', fileUrls.qr_code);
        }
      } catch (uploadErr) {
        console.error('One or more file uploads failed:', uploadErr);
        throw uploadErr; // bubble up to outer catch to show toast/error
      }

      // if we have at least one file URL, send them to the clinic upload endpoint
      if (Object.keys(fileUrls).length > 0) {
        const payload = {
          id: clinicId,
          ...fileUrls
        };

        console.log('Posting uploaded file URLs to /api/clinic/uploadFiles:', payload);

        const uploadRes = await axiosInstance.post('/api/clinic/uploadFiles', payload, {
          headers: { 'Content-Type': 'application/json' } // sending JSON with URLs
        });

        console.log('Upload files response:', uploadRes.data);
      } else {
        console.log('No files provided to upload to /api/clinic/uploadFiles');
      }

      // 3. Upload advice file if exists
      toast({ title: 'Clinic added!', status: 'success', duration: 2000, isClosable: true });
      setLoading(false);

      // If no advise file was uploaded, still show Done button
      if (!form.advice) {
        setShowDoneButton(true);
        setUploadedAdvices([{ id: 'no-file', advice_text: 'No advise file was uploaded' }]);
      }

      // Call the callback with the clinic ID if provided and no advice file
      if (onClinicCreated && clinicId && !form.advice) {
        onClinicCreated(clinicId);
      }
    } catch (error) {
      console.error('Error adding clinic or uploading files:', error);
      const serverMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      const { message, field } = humanizeApiError('/api/clinic/addClinic', serverMessage);
      if (field) {
        setFieldErrors({ [field === 'clinic_name' ? 'name' : field]: message });
      } else {
        setFieldErrors({});
      }
      toast({ title: 'Error', description: message, status: 'error', duration: 4000, isClosable: true, position: 'top' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent maxW="600px">
        <ModalHeader>Add Clinic</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <Flex direction="column" gap={3}>
              <FileUploadField
                label="Clinic Logo"
                id="clinic-logo-input"
                name="logo"
                onChange={handleLogoChange}
                selectedFile={form.logo}
                helperText="Square logo works best"
              />

              <Flex gap={2}>
                <Box flex="1">
                  <FormControl isRequired isInvalid={!!fieldErrors.name}>
                    <FormLabel>Clinic Name</FormLabel>
                    <Input name="name" value={form.name} onChange={handleChange} />
                    {fieldErrors.name && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {fieldErrors.name}
                      </Text>
                    )}
                  </FormControl>
                </Box>
                <Box flex="1">
                  <FormControl isRequired isInvalid={!!fieldErrors.email}>
                    <FormLabel>Email</FormLabel>
                    <Input name="email" value={form.email} onChange={handleChange} type="email" />
                    {fieldErrors.email && (
                      <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.email}</Text>
                    )}
                  </FormControl>
                </Box>
              </Flex>

              <Flex gap={2}>
                <Box flex="1">
                  <FormControl isRequired isInvalid={!!fieldErrors.phoneno}>
                    <FormLabel>Phone</FormLabel>
                    <Input name="phone" value={form.phone} onChange={handleChange} />
                    {fieldErrors.phoneno && (
                      <Text color="red.500" fontSize="sm" mt={1}>{fieldErrors.phoneno}</Text>
                    )}
                  </FormControl>
                </Box>
                <Box flex="1">
                  <FormControl>
                    <FormLabel>WhatsApp Number</FormLabel>
                    <Input name="whatsapp" value={form.whatsapp} onChange={handleChange} />
                  </FormControl>
                </Box>
              </Flex>

              <Box>
                <FormControl isRequired>
                  <FormLabel>Address</FormLabel>
                  <Input name="address" value={form.address} onChange={handleChange} />
                </FormControl>
              </Box>

              <Divider my={2} />
              <Box>
                <FormLabel>Payment Details</FormLabel>
                <Flex gap={2} mb={2}>
                  <Box flex="1">
                    <FormControl>
                      <FormLabel>UPI ID</FormLabel>
                      <Input
                        name="upi"
                        value={form.payment.upi}
                        onChange={handlePaymentChange}
                      />
                    </FormControl>
                  </Box>
                  <Box flex="1">
                    <FormControl>
                      <FormLabel>Account Holder Name</FormLabel>
                      <Input
                        name="account_holder"
                        value={form.payment.bank.account_holder}
                        onChange={handleBankChange}
                      />
                    </FormControl>
                  </Box>
                </Flex>
                <Flex gap={2} mb={2}>
                  <Box flex="1">
                    <FormControl>
                      <FormLabel>Account Number</FormLabel>
                      <Input
                        name="account_number"
                        value={form.payment.bank.account_number}
                        onChange={handleBankChange}
                      />
                    </FormControl>
                  </Box>
                  <Box flex="1">
                    <FormControl>
                      <FormLabel>Bank Name</FormLabel>
                      <Input
                        name="bank_name"
                        value={form.payment.bank.bank_name}
                        onChange={handleBankChange}
                      />
                    </FormControl>
                  </Box>
                </Flex>
                <Box>
                  <FormControl>
                    <FormLabel>IFSC Code</FormLabel>
                    <Input
                      name="ifsc_code"
                      value={form.payment.bank.ifsc_code}
                      onChange={handleBankChange}
                    />
                  </FormControl>
                </Box>
              </Box>



              <Divider my={2} />
              <Flex gap={2}>
                <Box flex="1">
                  <FileUploadField
                    label="Bar Code"
                    id="bar-code-input"
                    name="barCode"
                    onChange={handleBarCodeChange}
                    selectedFile={form.barCode}
                    helperText="Best at 400x400 px"
                  />
                </Box>
                <Box flex="1">
                  <FileUploadField
                    label="QR Code"
                    id="qr-code-input"
                    name="qrCode"
                    onChange={handleQrCodeChange}
                    selectedFile={form.qrCode}
                    helperText="High contrast recommended"
                  />
                </Box>
              </Flex>
            </Flex>
          </ModalBody>
          <ModalFooter>
            {(uploadedAdvices.length > 0 || showDoneButton) ? (
              <>
                <Button
                  colorScheme="blue"
                  onClick={() => {
                    // Call the callback with the clinic ID
                    if (onClinicCreated && currentClinicId) {
                      onClinicCreated(currentClinicId);
                    }
                    handleClose();
                  }}
                >
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button colorScheme="blue" type="submit" isLoading={loading}>Add Clinic</Button>
                <Button variant="ghost" onClick={handleClose} ml={3}>Cancel</Button>
              </>
            )}
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AddClinicModal;